import React, { useRef, useEffect, Suspense, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

// Model component with full cursor tracking
const AirplaneModel = ({ mouse, autoRotate }) => {
  const { scene } = useGLTF('/models/WoodenPlane.gltf');
  const ref = useRef();
  const [targetRotation] = useState(new THREE.Euler(0, Math.PI, 0));

  // Smooth rotation using useFrame
  useFrame(() => {
    if (ref.current) {
      // Auto-rotation (360 degrees)
      if (autoRotate) {
        ref.current.rotation.y += 0.01;
      }

      // Calculate target rotation based on normalized mouse position
      // Wider range for all directions
      const targetY = mouse.x * Math.PI; // Full left/right rotation
      const targetX = -mouse.y * Math.PI * 0.7; // Full up/down rotation

      // Smooth interpolation to target rotation
      ref.current.rotation.y += (targetY - ref.current.rotation.y) * 0.15;
      ref.current.rotation.x += (targetX - ref.current.rotation.x) * 0.15;
    }
  });

  return (
    <primitive
      ref={ref}
      object={scene}
      position={[0, 0, 0]}
      scale={[12, 12, 12]}
    />
  );
};

// WebGL support check
const WebGLCheck = () => {
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    try {
      const canvas = document.createElement('canvas');
      setIsSupported(
        !!window.WebGLRenderingContext &&
        !!canvas.getContext('webgl')
      );
    } catch (e) {
      setIsSupported(false);
    }
  }, []);

  if (!isSupported) {
    return (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        background: 'rgba(0,0,0,0.7)',
        color: 'white',
        zIndex: 10
      }}>
        WebGL not supported in your browser
      </div>
    );
  }
  return null;
};

const Airplane3D = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [autoRotate, setAutoRotate] = useState(true);
  const containerRef = useRef();

  const handleMouseMove = (event) => {
    if (!containerRef.current) return;

    // Get container dimensions
    const rect = containerRef.current.getBoundingClientRect();

    // Calculate normalized mouse position (-1 to 1)
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    setMouse({ x, y });
  };

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener('mousemove', handleMouseMove);
    return () => container?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        cursor: 'grab'
      }}
    >
      <WebGLCheck />

      <Canvas
        camera={{ fov: 75, position: [0, 0, 15] }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'transparent'
        }}
        dpr={[1, 2]}
        gl={{
          antialias: true,
          powerPreference: "high-performance",
          alpha: true
        }}
      >
        <ambientLight intensity={0.8} />
        <pointLight position={[5, 5, 5]} intensity={1.5} />
        <directionalLight
          position={[10, 10, 5]}
          intensity={0.5}
          castShadow
        />

        <Suspense fallback={null}>
          <AirplaneModel mouse={mouse} autoRotate={autoRotate} />
          <Environment preset="city" background={false} />
        </Suspense>

        <OrbitControls
          enabled={false}
          makeDefault={false}
        />
      </Canvas>
    </div>
  );
};

export default Airplane3D;
